/**
 * Lazy-loaded, decluttered email body for expanded Graph mail feed rows.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { SensitiveText } from "@/components/common/Sensitive";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import type { FeedGraphMailBody } from "@/types/feed";
import {
  cleanEmailHtml,
  cleanEmailPlainText,
  wrapEmailHtmlDocument,
} from "./graphMailBodyClean";

interface GraphMailBodyProps {
  mailboxId: string;
  messageId: string;
  className?: string;
}

export function GraphMailBody({
  mailboxId,
  messageId,
  className,
}: GraphMailBodyProps) {
  const [body, setBody] = useState<FeedGraphMailBody | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBody(null);

    api
      .getFeedGraphMailBody(mailboxId, messageId)
      .then((data) => {
        if (!cancelled) setBody(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load email body.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mailboxId, messageId]);

  const cleanedHtml = body?.body_html
    ? cleanEmailHtml(body.body_html)
    : null;
  const cleanedText = body?.body_text
    ? cleanEmailPlainText(body.body_text)
    : body?.body_preview
      ? cleanEmailPlainText(body.body_preview)
      : null;

  useEffect(() => {
    if (!cleanedHtml || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const doc = iframe.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(wrapEmailHtmlDocument(cleanedHtml));
    doc.close();

    const syncHeight = () => {
      const body = doc.body;
      const root = doc.documentElement;
      if (!body || !root) return;
      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        root.scrollHeight,
        root.offsetHeight,
      );
      iframe.style.height = `${Math.max(height, 96)}px`;
    };

    syncHeight();
    const observer = doc.body ? new ResizeObserver(syncHeight) : null;
    observer?.observe(doc.body);
    return () => observer?.disconnect();
  }, [cleanedHtml]);

  if (loading) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 py-3 text-xs text-muted-foreground",
          className,
        )}
      >
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Loading email…
      </div>
    );
  }

  if (error) {
    return (
      <p className={cn("py-2 text-xs text-muted-foreground", className)}>
        {error}
      </p>
    );
  }

  if (cleanedHtml) {
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {body?.from_address && (
          <p className="text-xs text-muted-foreground">
            <SensitiveText value={body.from_address} />
          </p>
        )}
        <iframe
          ref={iframeRef}
          title="Email body"
          sandbox="allow-same-origin"
          className="graph-mail-body-frame min-h-[6rem] w-full rounded-md border border-border/50 bg-background/40"
        />
      </div>
    );
  }

  if (cleanedText) {
    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {body?.from_address && (
          <p className="text-xs text-muted-foreground">
            <SensitiveText value={body.from_address} />
          </p>
        )}
        <div className="graph-mail-body-text rounded-md border border-border/50 bg-background/40 px-3 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words text-foreground/90">
          {cleanedText}
        </div>
      </div>
    );
  }

  return (
    <p className={cn("py-2 text-xs text-muted-foreground", className)}>
      No body available
      {body?.storage_tier && body.storage_tier !== "hot"
        ? ` (${body.storage_tier.replace(/_/g, " ")} tier).`
        : "."}
    </p>
  );
}
